import type { ProviderRow } from "@/app/types/Provider";
import { formatSkAppointmentLuxon } from "../../lib/date-time";
import React, { useEffect, useState } from "react";
import { BookingFormFields } from "./types";
import { getProviderDisplayName } from "../../lib/providerName";
import Button from "@/app/components/Button";
import ChipTooltip from "../../components/ChipTooltip";

export function BookingForm({
  slotUTC,
  providers,
  providerId,
  setSlotUTC,
  onBook,
  messageRequired = false,
  consentText,
  smsEnabled = false,
  contactHint,
}: {
  slotUTC: string;
  providers: ProviderRow[];
  providerId: string;
  setSlotUTC: (v: string) => void;
  onBook: (formData: BookingFormFields) => void;
  /** The clinic can insist the patient says why they are coming. */
  messageRequired?: boolean;
  /** The clinic's text the patient must tick before booking; none, no box. */
  consentText?: string | null;
  /** With SMS on, a phone can stand in for the email. */
  smsEnabled?: boolean;
  /** What the patient gets and where, from the clinic's settings. */
  contactHint: string;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // A tick only counts for the wording it was given to.
  useEffect(() => {
    setConsentChecked(false);
  }, [consentText]);

  const doctorName = getProviderDisplayName(
    providers.find((p) => p.id === providerId)
  );
  const summary = formatSkAppointmentLuxon(slotUTC);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "var(--surface)",
    border: "1px solid var(--line)",
    borderRadius: 13,
    padding: "14px 16px",
    fontFamily: "inherit",
    fontSize: 15,
    color: "var(--ink)",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 13.5,
    color: "var(--ink)",
    marginBottom: 7,
    display: "block",
  };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // The server refuses this too — the endpoint is public, so this is only
    // here to fail before the round trip rather than after it.
    if (messageRequired && !message.trim()) {
      setMessageError(true);
      return;
    }
    onBook({
      contactEmail: email,
      contactPhone: phone,
      firstName,
      lastName,
      message,
      // The text itself, not a yes: the server checks the patient saw what
      // the clinic currently asks for. Unticked, nothing is sent and book()
      // stops with the same alert as the other required fields.
      consentText: consentText && consentChecked ? consentText : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Summary bar */}
      <div
        style={{
          background: "var(--accent-tint)",
          borderRadius: 16,
          padding: "16px 18px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>
            {summary}
          </div>
          <button
            type="button"
            onClick={() => setSlotUTC("")}
            style={{
              marginTop: 6,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              color: "var(--accent-strong)",
              fontWeight: 700,
              fontSize: 13,
              textDecoration: "underline",
            }}
          >
            Zmeniť termín
          </button>
        </div>
        <div
          style={{ fontWeight: 600, fontSize: 14, color: "var(--ink-soft)" }}
        >
          {doctorName}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Meno pacienta <span style={{ color: "var(--accent-strong)" }}>*</span>
        </label>
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Meno"
          style={inputStyle}
          data-testid="booking-first-name-input"
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          Priezvisko pacienta{" "}
          <span style={{ color: "var(--accent-strong)" }}>*</span>
        </label>
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Priezvisko"
          style={inputStyle}
          data-testid="booking-last-name-input"
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          E-mail
          {!smsEnabled && (
            <span style={{ color: "var(--accent-strong)" }}> *</span>
          )}
        </label>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vas@email.sk"
          type="email"
          style={inputStyle}
          data-testid="booking-email-input"
        />
        {/* With SMS off the note is about the email alone; with SMS on it
            covers both fields, so it sits under the second. */}
        {!smsEnabled && <ContactHint text={contactHint} />}
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Telefón</label>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+421 900 000 000"
          style={inputStyle}
          data-testid="booking-phone-input"
        />
        {smsEnabled && <ContactHint text={contactHint} />}
      </div>
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>
          Správa
          {messageRequired && (
            <span style={{ color: "var(--accent-strong)" }}> *</span>
          )}
        </label>
        <textarea
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (messageError) setMessageError(false);
          }}
          rows={3}
          placeholder={
            messageRequired
              ? "Opíšte krátko, s čím prichádzate — pomôže to pripraviť sa na vašu návštevu."
              : "Sem môžete napísať napr. informácie o zdravotnom stave, dôvod návštevy, alebo iné otázky pre odborníka."
          }
          style={{
            ...inputStyle,
            resize: "vertical",
            lineHeight: 1.5,
            ...(messageError ? { borderColor: "var(--accent-strong)" } : {}),
          }}
          data-testid="booking-message-textarea"
        />
        {messageError && (
          <div
            style={{
              color: "var(--accent-strong)",
              fontSize: 13,
              marginTop: 6,
            }}
            data-testid="booking-message-error"
          >
            Táto ambulancia vyžaduje správu k rezervácii.
          </div>
        )}
      </div>
      {consentText && (
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              cursor: "pointer",
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 13,
              padding: "14px 16px",
            }}
          >
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{
                width: 18,
                height: 18,
                marginTop: 2,
                flexShrink: 0,
                accentColor: "var(--accent-strong)",
                cursor: "pointer",
              }}
              data-testid="booking-consent-checkbox"
            />
            <span
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                color: "var(--ink)",
                whiteSpace: "pre-wrap",
              }}
              data-testid="booking-consent-text"
            >
              {consentText}
              <span style={{ color: "var(--accent-strong)" }}> *</span>
            </span>
          </label>
        </div>
      )}
      <Button
        variant="themed"
        type="submit"
        data-testid="booking-submit-button"
        style={{
          width: "100%",
          fontSize: 16,
          padding: 16,
          borderRadius: 14,
          fontWeight: 700,
        }}
      >
        Objednať sa
      </Button>
    </form>
  );
}

function ContactHint({ text }: { text: string }) {
  return (
    <div
      style={{ position: "relative", left: -10 }}
      data-testid="booking-contact-hint"
    >
      <ChipTooltip text={text} bg="var(--accent)" color="var(--accent-ink)" />
    </div>
  );
}
